/**
 * AW-LDQ-003 -- API payloads to panel props.
 *
 * WHY AN ADAPTER AND NOT A PASS-THROUGH
 * -------------------------------------
 * The original panels were written against a projection shape, and the
 * Runs API answers in a different one. Two concrete mismatches, not
 * cosmetic:
 *
 *   `QuoteValuePublic` names its keys `quote_field_id` and
 *   `prospect_id`; the panel reads `field_id` and `subject_id`. A
 *   pass-through renders nothing at all.
 *
 *   `RunProspectPublic` carries `lead_id` -- a customer identity the
 *   Quote and Discovery surfaces have no business holding. It is
 *   dropped here, by construction, because these adapters build NEW
 *   objects from named keys rather than spreading the response.
 *
 * That second property is the point of the whole module: nothing
 * reaches a panel unless it is written out below by name. An extra key
 * the backend starts returning tomorrow cannot leak through.
 *
 * `WORKSPACE_TOOL_FIELD_ALLOWLISTS` in `projectionContract.js` is the
 * written-down version of the same rule, and the keys below are a
 * subset of it. It is not applied as a runtime validator because
 * `validateProjectionEnvelope` requires `provenance.kind === "fake"` --
 * it was built to police the fixture tier, and real Production data
 * cannot satisfy it. Changing that contract is a separate decision; a
 * test asserts the correspondence instead.
 *
 * Pure functions. No network, no state, no React.
 */

/** The subject every panel synthesises rather than reads. */
export const HOUSEHOLD_SUBJECT = Object.freeze({
  id: "household",
  name: "Household",
  kind: "household",
});

/**
 * Quote option indices.
 *
 * A product constant, not data: `QuoteValueEntry.option_index` is
 * documented as "1, 2, or 3" and no endpoint publishes a per-script
 * option count. Derived here rather than hardcoded at the call site so
 * there is one place to change when one does.
 */
export const QUOTE_OPTION_INDICES = Object.freeze([1, 2, 3]);

function sortOrder(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * `RunProspectPublic[]` -> the panels' subject list.
 *
 * Household is prepended because the API has no row for it: it is a
 * derived subject both panels expect to find first. `kind` is
 * synthesised for the same reason -- the API models prospects only, so
 * every row IS a prospect.
 *
 * `lead_id`, `run_id` and `created_at` are not copied.
 */
export function toSubjects(prospects) {
  const rows = Array.isArray(prospects) ? prospects : [];
  const ordered = rows
    .slice()
    .sort((a, b) => sortOrder(a?.sort_order) - sortOrder(b?.sort_order))
    .map((p, index) => ({
      id: p?.id,
      name: p?.name,
      kind: "prospect",
      sort_order: sortOrder(p?.sort_order) || index,
    }))
    .filter((p) => p.id);
  return [HOUSEHOLD_SUBJECT, ...ordered];
}

/**
 * Discovery definitions -> the panel's `fields`.
 *
 * `options` is normalised to an array because a dropdown with a null
 * options list would throw on `.map`, and a definition that arrived
 * without one is a configuration gap rather than a render failure.
 */
export function toDiscoveryFields(definitions) {
  const rows = Array.isArray(definitions) ? definitions : [];
  return rows
    .filter((f) => f && f.id)
    .map((f) => ({
      id: f.id,
      label: f.label,
      field_type: f.field_type,
      required: Boolean(f.required),
      scope: f.scope,
      sort_order: sortOrder(f.sort_order),
      options: Array.isArray(f.options) ? f.options : [],
    }));
}

/** `RunDataPublic[]` -> the panel's `values`. Drops id/run_id/updated_at. */
export function toDiscoveryValues(rows) {
  const data = Array.isArray(rows) ? rows : [];
  return data
    .filter((d) => d && d.field_id)
    .map((d) => ({
      field_id: d.field_id,
      subject_id: d.subject_id || HOUSEHOLD_SUBJECT.id,
      value: d.value,
    }));
}

/** Quote definitions -> the panel's `fields`. */
export function toQuoteFields(definitions) {
  const rows = Array.isArray(definitions) ? definitions : [];
  return rows
    .filter((f) => f && f.id)
    .map((f) => ({
      id: f.id,
      label: f.label,
      kind: f.kind,
      field_type: f.field_type,
      sort_order: sortOrder(f.sort_order),
      options: Array.isArray(f.options) ? f.options : [],
      include_in_household: Boolean(f.include_in_household),
    }));
}

/**
 * `QuoteValuePublic[]` -> the panel's `values`.
 *
 * The rename that makes this module necessary:
 *   quote_field_id -> field_id
 *   prospect_id    -> subject_id
 */
export function toQuoteValues(rows) {
  const data = Array.isArray(rows) ? rows : [];
  return data
    .filter((v) => v && v.quote_field_id)
    .map((v) => ({
      field_id: v.quote_field_id,
      subject_id: v.prospect_id || HOUSEHOLD_SUBJECT.id,
      option_index: Number.isFinite(v.option_index) ? v.option_index : 1,
      value: v.value,
    }));
}
